import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import inventoryService from '../services/inventoryService';

interface SummaryStats {
  totalInventoryItems: number;
  shelfLifeAlerts: {
    total: number;
    critical: number;
    warning: number;
  };
  lowStockAlerts: {
    total: number;
    critical: number;
    warning: number;
  };
  healthyStock: number;
}

const InventorySummaryWidget: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<SummaryStats>({
    totalInventoryItems: 0,
    shelfLifeAlerts: { total: 0, critical: 0, warning: 0 },
    lowStockAlerts: { total: 0, critical: 0, warning: 0 },
    healthyStock: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSummaryStats();
  }, []);

  const loadSummaryStats = async () => {
    setLoading(true);
    try {
      // Get inventory alerts and summary data
      const [alerts, inventorySummary] = await Promise.all([
        inventoryService.getInventoryAlerts(),
        inventoryService.getInventorySummary({ pageSize: 1000 }), // Get all products
      ]);

      // Calculate shelf life alerts (expired + expiring soon)
      const shelfLifeAlerts = alerts.filter(
        (alert) => alert.type === 'expired' || alert.type === 'expiring_soon'
      );
      const shelfLifeCritical = shelfLifeAlerts.filter(
        (alert) => alert.severity === 'critical'
      ).length;
      const shelfLifeWarning = shelfLifeAlerts.filter(
        (alert) => alert.severity === 'warning'
      ).length;

      // Calculate low stock alerts
      const lowStockAlerts = alerts.filter((alert) => alert.type === 'low_stock');
      const lowStockCritical = lowStockAlerts.filter(
        (alert) => alert.severity === 'critical'
      ).length;
      const lowStockWarning = lowStockAlerts.filter((alert) => alert.severity === 'warning').length;

      // Calculate healthy stock (products with good status)
      const healthyStock = inventorySummary.data.filter(
        (item) => item.status === 'good' && !item.belowThreshold
      ).length;

      // Total inventory items (sum of all products with inventory)
      const totalInventoryItems = inventorySummary.data.length;

      setStats({
        totalInventoryItems,
        shelfLifeAlerts: {
          total: shelfLifeAlerts.length,
          critical: shelfLifeCritical,
          warning: shelfLifeWarning,
        },
        lowStockAlerts: {
          total: lowStockAlerts.length,
          critical: lowStockCritical,
          warning: lowStockWarning,
        },
        healthyStock,
      });
    } catch (err) {
      console.error('Error loading summary stats:', err);
      setError('Failed to load inventory summary');
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (type: string) => {
    switch (type) {
      case 'total':
        navigate('/inventory');
        break;
      case 'shelf-life':
        navigate('/inventory-alerts?filter=shelf-life');
        break;
      case 'low-stock':
        navigate('/inventory-alerts?filter=low-stock');
        break;
      case 'healthy':
        navigate('/inventory');
        break;
    }
  };

  if (loading) {
    return (
      <div className="inventory-summary-widget">
        <h3>Inventory Summary</h3>
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="inventory-summary-widget">
        <h3>Inventory Summary</h3>
        <div className="error">{error}</div>
      </div>
    );
  }

  return (
    <div className="inventory-summary-widget">
      <h3>Inventory Summary</h3>
      <div className="summary-grid">
        <div
          className="summary-card clickable healthy-card"
          onClick={() => handleCardClick('healthy')}
        >
          <div className="summary-card-header">
            <span className="summary-icon">✅</span>
            <span className="summary-title">Healthy Stock</span>
          </div>
          <div className="summary-value">{stats.healthyStock}</div>
          <div className="summary-subtitle">Well-stocked items</div>
        </div>

        <div className="summary-card clickable" onClick={() => handleCardClick('total')}>
          <div className="summary-card-header">
            <span className="summary-icon">📦</span>
            <span className="summary-title">Total Items</span>
          </div>
          <div className="summary-value">{stats.totalInventoryItems}</div>
          <div className="summary-subtitle">Products in inventory</div>
        </div>

        <div
          className="summary-card clickable low-stock-card"
          onClick={() => handleCardClick('low-stock')}
        >
          <div className="summary-card-header">
            <span className="summary-icon">📉</span>
            <span className="summary-title">Low Stock Alerts</span>
          </div>
          <div className="summary-value">
            {stats.lowStockAlerts.total}
            {stats.lowStockAlerts.critical > 0 && (
              <span className="alert-badge critical">
                Critical: {stats.lowStockAlerts.critical}
              </span>
            )}
            {stats.lowStockAlerts.warning > 0 && (
              <span className="alert-badge warning">Warning: {stats.lowStockAlerts.warning}</span>
            )}
          </div>
          <div className="summary-subtitle">Below threshold</div>
        </div>

        <div
          className="summary-card clickable shelf-life-card"
          onClick={() => handleCardClick('shelf-life')}
        >
          <div className="summary-card-header">
            <span className="summary-icon">⏰</span>
            <span className="summary-title">Shelf Life Alerts</span>
          </div>
          <div className="summary-value">
            {stats.shelfLifeAlerts.total}
            {stats.shelfLifeAlerts.critical > 0 && (
              <span className="alert-badge critical">
                Critical: {stats.shelfLifeAlerts.critical}
              </span>
            )}
            {stats.shelfLifeAlerts.warning > 0 && (
              <span className="alert-badge warning">Warning: {stats.shelfLifeAlerts.warning}</span>
            )}
          </div>
          <div className="summary-subtitle">Expiring/expired items</div>
        </div>
      </div>
    </div>
  );
};

export default InventorySummaryWidget;
